import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import api, { servicesAPI, serviceTypesAPI, serviceChatAPI, teamAPI, rolesAPI, groupsAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import SchedulerGrid from '../components/SchedulerGrid';
import AvailabilityModal from '../components/AvailabilityModal';
import { Plus, Calendar, Trash2, Settings, History, Check, X, Clock, Copy, MessageSquare, LayoutGrid, CalendarOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { canAdmin, canEdit, canSchedule } from '../utils/permissions';
import DeleteButton from '../components/ui/DeleteButton';
import { useModalHistory } from '../hooks/useModalHistory';

const formatTime12hr = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hr = hours % 12 || 12;
  return `${hr}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

const Services = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const serviceModal = useModalHistory(isDialogOpen, setIsDialogOpen);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  const [myAssignments, setMyAssignments] = useState({});
  const [discUnreadMap, setDiscUnreadMap] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    service_type: '',
    date: '',
    time: '',
    notes: '',
  });

  const PAGE_SIZE = 20;
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);
  const cursorRef = useRef(null);  // C1: next_cursor for start_after pagination
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const filterRef = useRef({ showPast: false, typeFilter: 'all' });
  const loadedServiceIdsRef = useRef(new Set()); // tracks REST-loaded IDs for Firestore dedup
  const typeFilterRef = useRef(typeFilter); // stable ref so listener doesn't recreate on filter change

  // Keep refs in sync with state
  filterRef.current = { showPast, typeFilter };
  typeFilterRef.current = typeFilter;

  const loadMeta = useCallback(async () => {
    try {
      const [typesRes, assignRes] = await Promise.all([
        serviceTypesAPI.getAll(),
        api.get('/services/my-assignments').catch(() => ({ data: [] })),
      ]);
      setServiceTypes(typesRes.data);
      const map = {};
      (assignRes.data || []).forEach(a => { map[a.service_id] = a; });
      setMyAssignments(map);
    } catch {}
  }, []);

  const loadServices = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;

    loadingRef.current = true;
    if (reset) { setLoading(true); skipRef.current = 0; cursorRef.current = null; }
    else setLoadingMore(true);

    try {
      const { showPast: past, typeFilter: stype } = filterRef.current;
      const _d = new Date();
      const localDate = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
      const res = await servicesAPI.getAll({ cursor: cursorRef.current || undefined, limit: PAGE_SIZE, past, service_type: stype, local_date: localDate });
      const { services: items, has_more: more, next_cursor } = res.data;

      if (reset) {
        setServices(items);
        loadedServiceIdsRef.current = new Set(items.map(s => s.service_id));
      } else {
        setServices(prev => [...prev, ...items]);
        items.forEach(s => loadedServiceIdsRef.current.add(s.service_id));
      }

      skipRef.current = (skipRef.current || 0) + items.length;
      cursorRef.current = next_cursor || null;
      hasMoreRef.current = more;
      setHasMore(more);

      if (items.length > 0) {
        const payload = items.map(s => ({ service_id: s.service_id }));
        serviceChatAPI.getUnreadBulk(payload)
          .then(r => setDiscUnreadMap(prev => ({ ...prev, ...(r.data || {}) })))
          .catch(() => {});
      }
    } catch {
      toast.error('Failed to load services');
    } finally {
      loadingRef.current = false;
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  // Load meta (types, assignments) once
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Reset + reload when filters change
  useEffect(() => { loadServices(true); }, [showPast, typeFilter, loadServices]);

  // Real-time: reflect service additions, updates, and deletions without a page refresh.
  // Only active for the "upcoming" view — past services change rarely so no listener needed.
  //
  // scheduled_viewer users get a filtered listener: `assigned_member_ids array-contains memberId`
  // scopes the Firestore query to only their assigned services, so no all-services leak.
  // Requires Index D (assigned_member_ids Arrays + date Ascending).
  useEffect(() => {
    if (showPast || !firestoreDb) return;

    const _d = new Date();
    const localToday = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;

    // Build the Firestore query — scoped by assignment for scheduled_viewer users
    let fsQuery;
    if (user?.permission_level === 'scheduled_viewer') {
      if (!user.member_id) return; // member_id needed to scope the query; skip if missing
      fsQuery = query(
        collection(firestoreDb, 'services'),
        where('assigned_member_ids', 'array-contains', user.member_id),
        where('date', '>=', localToday),
        orderBy('date', 'asc'),
        limit(90)
      );
    } else {
      // Cap at 90 docs (≈3 months of weekly services) — prevents reading unlimited future services.
      fsQuery = query(
        collection(firestoreDb, 'services'),
        where('date', '>=', localToday),
        orderBy('date', 'asc'),
        limit(90)
      );
    }

    let unsubscribe;
    try {
      unsubscribe = onSnapshot(fsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const doc = change.doc.data();
          if (!doc.service_id || !doc.date) return;

          if (change.type === 'added' || change.type === 'modified') {
            setServices(prev => {
              const exists = prev.some(s => s.service_id === doc.service_id);
              if (exists) {
                return prev.map(s =>
                  s.service_id === doc.service_id ? { ...s, ...doc, can_edit: s.can_edit } : s
                );
              }
              // Truly new service — respect current typeFilter
              if (!loadedServiceIdsRef.current.has(doc.service_id)) {
                const tf = typeFilterRef.current;
                if (tf !== 'all' && doc.service_type !== tf) return prev;
                loadedServiceIdsRef.current.add(doc.service_id);
                const updated = [{ ...doc, can_edit: false }, ...prev];
                return updated.sort((a, b) =>
                  a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || '')
                );
              }
              return prev;
            });
          } else if (change.type === 'removed') {
            loadedServiceIdsRef.current.delete(doc.service_id);
            setServices(prev => prev.filter(s => s.service_id !== doc.service_id));
          }
        });
      }, () => { /* Firestore unavailable — silent fail */ });
    } catch { /* Firestore not configured */ }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [showPast, user?.permission_level, user?.member_id]); // re-subscribe when user role or past toggle changes

  // Infinite scroll sentinel
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
        loadServices(false);
      }
    }, { rootMargin: '200px' });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.name || !payload.name.trim()) payload.name = null;
      await servicesAPI.create(payload);
      toast.success('Service created');
      serviceModal.close();
      setFormData({ name: '', service_type: '', date: '', time: '', notes: '' });
      loadServices(true);
      loadMeta();
    } catch (error) {
      toast.error('Failed to create service');
    }
  };

  const handleDelete = async (serviceId) => {
    try {
      await servicesAPI.delete(serviceId);
      toast.success('Service deleted');
      loadServices(true);
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const [responding, setResponding] = useState(null); // { id: serviceId, action: 'accept'|'decline' }

  // ── Scheduler Grid ─────────────────────────────────────────────────────────
  const gridView = new URLSearchParams(location.search).get('view') === 'grid';
  const setGridView = useCallback((val) => {
    const next = typeof val === 'function' ? val(new URLSearchParams(location.search).get('view') === 'grid') : val;
    navigate(next ? '/services?view=grid' : '/services', { replace: true });
  }, [navigate, location.search]);

  // Exit grid view automatically if viewport shrinks below md (768px)
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 768) setGridView(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setGridView]);

  // Suppress body padding-bottom (from index.css for bottom nav) in grid mode
  useEffect(() => {
    if (gridView) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingBottom = '0';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingBottom = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingBottom = '';
    };
  }, [gridView]);
  const [gridServices, setGridServices] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [allMembersForGrid, setAllMembersForGrid] = useState([]);
  const [allBlocks, setAllBlocks] = useState([]);
  const [showAvailability, setShowAvailability] = useState(false);
  const [savingServices, setSavingServices] = useState(new Set());
  const gridServicesRef = useRef([]);
  const saveTimersRef = useRef({});
  const gridSnapshotUnsubRef = useRef(null); // cleanup fn for grid Firestore listeners

  // Keep ref in sync with grid services state (needed for debounced save closures)
  useEffect(() => { gridServicesRef.current = gridServices; }, [gridServices]);

  // Real-time collaborative sync for the Scheduler Grid.
  // Watches team_assignments on every loaded service document so changes from the People tab
  // or another user's Scheduler Grid are reflected immediately without a page reload.
  useEffect(() => {
    // Tear down any existing listeners when leaving grid view or before re-subscribing
    if (!gridView || gridServicesRef.current.length === 0 || !firestoreDb) {
      if (gridSnapshotUnsubRef.current) {
        gridSnapshotUnsubRef.current();
        gridSnapshotUnsubRef.current = null;
      }
      return;
    }
    // Already subscribed to the current set — don't recreate
    if (gridSnapshotUnsubRef.current) return;

    const ids = gridServicesRef.current.map(s => s.service_id);
    const BATCH = 30; // Firestore 'in' operator max
    const unsubscribers = [];

    for (let i = 0; i < ids.length; i += BATCH) {
      const batch = ids.slice(i, i + BATCH);
      try {
        const q = query(collection(firestoreDb, 'services'), where('service_id', 'in', batch));
        const unsub = onSnapshot(q, (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            // 'added' fires on the initial snapshot for all existing docs — skip those.
            // Only 'modified' means another user actually changed something.
            if (change.type !== 'modified') return;
            const doc = change.doc.data();
            const incomingTA = doc.team_assignments;
            if (!incomingTA || !Array.isArray(incomingTA)) return;
            const svcId = doc.service_id;
            // Skip if we have a pending local save for this service (user is mid-edit)
            if (saveTimersRef.current[svcId]) return;
            const current = gridServicesRef.current.find(s => s.service_id === svcId);
            if (!current) return;
            if (JSON.stringify(incomingTA) === JSON.stringify(current.team_assignments)) return;
            setGridServices(prev => prev.map(s =>
              s.service_id === svcId ? { ...s, team_assignments: incomingTA } : s
            ));
          });
        }, () => { /* Firestore unavailable — silent fail */ });
        unsubscribers.push(unsub);
      } catch { /* Firestore not configured */ }
    }

    gridSnapshotUnsubRef.current = () => unsubscribers.forEach(u => u());
    return () => {
      if (gridSnapshotUnsubRef.current) {
        gridSnapshotUnsubRef.current();
        gridSnapshotUnsubRef.current = null;
      }
    };
  }, [gridView, gridServices.length]); // re-subscribe only when view toggles or service count changes

  // Flush a pending debounced save immediately (called before navigating to service detail)
  const flushServiceSave = useCallback(async (serviceId) => {
    const timerId = saveTimersRef.current[serviceId];
    if (!timerId) return;
    clearTimeout(timerId);
    delete saveTimersRef.current[serviceId];
    const service = gridServicesRef.current.find(s => s.service_id === serviceId);
    if (!service) return;
    try {
      await servicesAPI.updateTeamAssignments(serviceId, service.team_assignments);
    } catch {
      toast.error('Failed to save assignment');
    } finally {
      setSavingServices(prev => { const n = new Set(prev); n.delete(serviceId); return n; });
    }
  }, []);

  // Flush all pending saves when navigating away from the grid
  useEffect(() => {
    return () => {
      Object.keys(saveTimersRef.current).forEach(serviceId => {
        clearTimeout(saveTimersRef.current[serviceId]);
        const service = gridServicesRef.current.find(s => s.service_id === serviceId);
        if (service) {
          servicesAPI.updateTeamAssignments(serviceId, service.team_assignments).catch(() => {});
        }
      });
      saveTimersRef.current = {};
    };
  }, []);

  const loadGridData = useCallback(async () => {
    setGridLoading(true);
    try {
      const _d = new Date();
      const localDate = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
      const [svcRes, teamRes, rolesRes, groupsRes, blocksRes] = await Promise.all([
        servicesAPI.getAll({ skip: 0, limit: 500, past: false, service_type: typeFilter === 'all' ? undefined : typeFilter, local_date: localDate }),
        teamAPI.getAll({ limit: 9999 }),
        rolesAPI.getAll(),
        groupsAPI.getAll(),
        api.get('/availability/team').catch(() => ({ data: { blocks: [] } })),
      ]);
      setGridServices(svcRes.data.services || []);
      setAllMembersForGrid(teamRes.data.members || []);
      setAllRoles(rolesRes.data || []);
      setAllGroups(groupsRes.data || []);
      setAllBlocks(blocksRes.data.blocks || []);
    } catch {
      toast.error('Failed to load scheduler data');
    } finally {
      setGridLoading(false);
    }
  }, [typeFilter]);

  // Load grid data when entering grid view (or when type filter changes while in grid)
  useEffect(() => {
    if (gridView) loadGridData();
  }, [gridView, loadGridData]);

  const handleGridRemoveTeam = useCallback((serviceId, groupName) => {
    setGridServices(prev => {
      const updated = prev.map(s => {
        if (s.service_id !== serviceId) return s;
        return {
          ...s,
          team_assignments: (s.team_assignments || []).filter(ta => ta.group !== groupName),
        };
      });
      gridServicesRef.current = updated;
      return updated;
    });
    setSavingServices(prev => new Set([...prev, serviceId]));
    clearTimeout(saveTimersRef.current[serviceId]);
    saveTimersRef.current[serviceId] = setTimeout(async () => {
      const service = gridServicesRef.current.find(s => s.service_id === serviceId);
      if (!service) return;
      try {
        await servicesAPI.updateTeamAssignments(serviceId, service.team_assignments);
      } catch {
        toast.error('Failed to remove team');
      } finally {
        setSavingServices(prev => { const n = new Set(prev); n.delete(serviceId); return n; });
      }
    }, 800);
  }, []);

  const handleGridAddTeam = useCallback((serviceId, groupName) => {
    const groupRoles = allRoles.filter(r => r.group === groupName);
    const newEntry = {
      group: groupName,
      assignments: groupRoles.map(r => ({ role: r.name, member_id: '', status: '' })),
    };
    setGridServices(prev => {
      const updated = prev.map(s => {
        if (s.service_id !== serviceId) return s;
        // Avoid duplicate — only add if not already present
        if ((s.team_assignments || []).some(ta => ta.group === groupName)) return s;
        return { ...s, team_assignments: [...(s.team_assignments || []), newEntry] };
      });
      gridServicesRef.current = updated;
      return updated;
    });
    // Debounced save
    setSavingServices(prev => new Set([...prev, serviceId]));
    clearTimeout(saveTimersRef.current[serviceId]);
    saveTimersRef.current[serviceId] = setTimeout(async () => {
      const service = gridServicesRef.current.find(s => s.service_id === serviceId);
      if (!service) return;
      try {
        await servicesAPI.updateTeamAssignments(serviceId, service.team_assignments);
      } catch {
        toast.error('Failed to add team');
      } finally {
        setSavingServices(prev => { const n = new Set(prev); n.delete(serviceId); return n; });
      }
    }, 800);
  }, [allRoles]);

  const handleGridAssign = useCallback((serviceId, groupName, roleName, memberId) => {
    // Optimistic local update
    setGridServices(prev => {
      const updated = prev.map(s => {
        if (s.service_id !== serviceId) return s;
        return {
          ...s,
          team_assignments: (s.team_assignments || []).map(ta => {
            if (ta.group !== groupName) return ta;
            return {
              ...ta,
              assignments: (ta.assignments || []).map(a =>
                a.role === roleName ? { ...a, member_id: memberId, status: memberId === a.member_id ? (a.status || '') : '' } : a
              ),
            };
          }),
        };
      });
      gridServicesRef.current = updated;
      return updated;
    });

    // Debounced API save (800ms)
    setSavingServices(prev => new Set([...prev, serviceId]));
    clearTimeout(saveTimersRef.current[serviceId]);
    saveTimersRef.current[serviceId] = setTimeout(async () => {
      const service = gridServicesRef.current.find(s => s.service_id === serviceId);
      if (!service) return;
      try {
        await servicesAPI.updateTeamAssignments(serviceId, service.team_assignments);
      } catch {
        toast.error('Failed to save assignment');
      } finally {
        setSavingServices(prev => { const n = new Set(prev); n.delete(serviceId); return n; });
      }
    }, 800);
  }, []);

  const handleRespond = async (serviceId, status, action) => {
    flushSync(() => setResponding({ id: serviceId, action }));
    try {
      await api.post('/services/respond', { service_id: serviceId, status });
      if (status !== 'pending') toast.success(status === 'accepted' ? 'Accepted' : 'Declined');
      setMyAssignments(prev => ({ ...prev, [serviceId]: { ...prev[serviceId], status } }));
    } catch (error) {
      toast.error('Failed to respond');
    } finally {
      setResponding(null);
    }
  };

  const handleClone = async (serviceId) => {
    try {
      const res = await servicesAPI.clone(serviceId);
      toast.success('Service cloned');
      navigate(`/services/${res.data.service_id}`);
    } catch (error) {
      toast.error('Failed to clone service');
    }
  };

  // Bug 1 fix: override discUnreadMap with sessionStorage entries written by ServiceDetail
  // when the user reads a discussion — prevents stale badges during the API race window.
  const getEffectiveUnread = useCallback((svcId) => {
    const raw = discUnreadMap[svcId] || 0;
    if (raw === 0) return 0;
    try {
      const cleared = JSON.parse(sessionStorage.getItem('clearedDiscussions') || '{}');
      const ts = cleared[svcId];
      if (ts && Date.now() - ts < 300000) return 0; // 5-minute TTL
    } catch { /* ignore */ }
    return raw;
  }, [discUnreadMap]);

  // Use local date for isPast comparison on assignment buttons
  const _d = new Date();
  const today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;

  return (
    <>
    <div className={gridView ? 'h-[100dvh] overflow-hidden bg-slate-50 flex flex-col' : 'bg-slate-50 pb-20 md:pb-0'}>
      <Navbar />
      <div className={`${gridView ? 'flex-1 min-h-0 flex flex-col px-4 sm:px-6 pt-5 sm:pt-8 pb-20' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8'}`}>
        {/* Header */}
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
          <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900">Services</h1>
                {/* Small grid toggle inline with the title */}
                {/* Grid toggle: visible to all except scheduled_viewer (they only see their own services) */}
                {user?.permission_level !== 'scheduled_viewer' && (
                <button
                  onClick={() => setGridView(v => !v)}
                  data-testid="scheduler-grid-toggle"
                  title={gridView ? 'Switch to card view' : 'Scheduler grid view'}
                  className={`h-7 w-7 hidden md:flex items-center justify-center rounded-md border transition-all duration-200 flex-shrink-0 ${
                    gridView
                      ? 'bg-green-700 text-white border-green-700'
                      : 'bg-white text-slate-400 border-slate-300 hover:text-green-700 hover:border-green-400'
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </button>
                )}
              </div>
              <p className="mt-1 text-sm sm:text-base text-slate-600">Manage your worship services and events</p>
            </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowAvailability(true)}
              data-testid="my-availability-btn-services"
              className="h-9 sm:h-10 px-3 sm:px-4 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 transition-all duration-200 shadow-sm font-medium flex items-center gap-1.5 text-sm whitespace-nowrap"
            >
              <CalendarOff className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">My Availability</span>
            </button>
          {canSchedule(user) && (
            <div className="flex gap-2 sm:gap-3">
              {canAdmin(user) && (
                <button
                  onClick={() => navigate('/service-types')}
                  data-testid="edit-service-types-btn"
                  className="h-9 sm:h-10 px-3 sm:px-6 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  <Settings className="w-4 h-4 flex-shrink-0" />
                  <span className="sm:hidden">Service Types</span>
                  <span className="hidden sm:inline">Edit Service Types</span>
                </button>
              )}
              <button
                  onClick={() => serviceModal.open()}
                  data-testid="add-service-btn"
                  className="h-9 sm:h-10 px-3 sm:px-6 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  Add Service
                </button>
            </div>
          )}
          </div>  {/* end outer flex gap-2 */}
        </div>

        {/* Filter & Toggle — hidden in grid view */}
        {!gridView && (
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="service-type-filter" className="w-full sm:w-56 bg-white h-10">
              <SelectValue placeholder="All Service Types" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              {serviceTypes.map((t) => (
                <SelectItem key={t.service_type_id} value={t.name}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setShowPast(p => !p)}
            data-testid="toggle-past-btn"
            className={`h-10 px-4 rounded-full border text-sm font-medium flex items-center gap-2 transition-all duration-200 flex-shrink-0 ${
              showPast
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
          >
            <History className="w-4 h-4" />
            Past
          </button>
        </div>
        )}

        {/* ── Grid View ─────────────────────────────────────────────────────── */}
        {gridView ? (
          gridLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto" />
                <p className="text-sm text-slate-500 mt-3">Loading scheduler data…</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              <SchedulerGrid
                services={gridServices}
                roles={allRoles}
                groups={allGroups}
                allMembers={allMembersForGrid}
                allBlocks={allBlocks}
                onAssign={handleGridAssign}
                onAddTeam={handleGridAddTeam}
                onRemoveTeam={handleGridRemoveTeam}
                onFlushSave={flushServiceSave}
                savingServices={savingServices}
                readOnly={!canSchedule(user)}
              />
            </div>
          )
        ) : (
          /* ── Card List (default view) ─────────────────────────────────── */
          loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{showPast ? 'No past services' : typeFilter !== 'all' ? 'No upcoming services match this type' : 'No upcoming services'}</p>
              {canSchedule(user) && typeFilter === 'all' && (
                <button
                  onClick={() => serviceModal.open()}
                  className="mt-3 text-sm text-green-700 hover:text-green-800 font-medium"
                >
                  Create your first service
                </button>
              )}
            </div>
          ) : (
            <>
            <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 lg:gap-6">
              {services.map((service) => {
                const sDate = service.date ? new Date(service.date + 'T' + (service.time || '00:00')) : null;
                const nameDiffersFromType = service.name && service.name.trim().toLowerCase() !== (service.service_type || '').trim().toLowerCase();
                const assignment = myAssignments[service.service_id];
                const allAssignments = (service.team_assignments || []).flatMap(g => g.assignments || []).filter(a => a.member_id);
                const uniqueMembers = [...new Map(allAssignments.map(a => [a.member_id, a])).values()];
                const accepted = uniqueMembers.filter(a => a.status?.toLowerCase() === 'accepted').length;
                const declined = uniqueMembers.filter(a => a.status?.toLowerCase() === 'declined').length;
                const pending = uniqueMembers.filter(a => !a.status || a.status.toLowerCase() === 'pending').length;
                const total = uniqueMembers.length;
                return (
                  <div
                    key={service.service_id}
                    data-testid={`service-item-${service.service_id}`}
                    onClick={() => navigate(`/services/${service.service_id}`)}
                    className={`bg-white border rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${assignment ? 'border-green-200 ring-1 ring-green-100' : 'border-slate-200 hover:border-green-200'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight" data-testid={`service-date-${service.service_id}`}>
                          {sDate ? format(sDate, 'dd MMM') : 'No date'}{' '}
                          <span className="text-slate-400 font-medium">@</span>{' '}
                          <span className="text-slate-700">{formatTime12hr(service.time)}</span>
                        </h3>
                        <p className="text-sm text-slate-600 mt-0.5" data-testid={`service-type-${service.service_id}`}>{service.service_type}</p>
                        {nameDiffersFromType && (
                          <p className="text-sm text-slate-500 mt-0.5" data-testid={`service-name-${service.service_id}`}>{service.name}</p>
                        )}
                        {(total > 0 || getEffectiveUnread(service.service_id) > 0) && (
                          <div className="flex items-center gap-2 text-xs mt-1.5 flex-wrap">
                            {total > 0 && (
                              <>
                                <span className="flex items-center gap-0.5 text-emerald-600" data-testid={`assignment-stats-${service.service_id}`}><Check className="w-3 h-3" />{accepted}</span>
                                <span className="flex items-center gap-0.5 text-rose-500"><X className="w-3 h-3" />{declined}</span>
                                <span className="flex items-center gap-0.5 text-amber-500"><Clock className="w-3 h-3" />{pending}</span>
                              </>
                            )}
                            {total > 0 && getEffectiveUnread(service.service_id) > 0 && (
                              <span className="text-slate-200">|</span>
                            )}
                            {getEffectiveUnread(service.service_id) > 0 && (
                              <div className="flex items-center gap-1" data-testid={`discussion-unread-dot-${service.service_id}`}>
                                <span className="relative flex h-2 w-2 flex-shrink-0">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                                </span>
                                <MessageSquare className="w-3 h-3 text-green-500 flex-shrink-0" />
                                <span className="font-medium text-green-600">{getEffectiveUnread(service.service_id)} new in Discussion</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        {canSchedule(user) && (
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClone(service.service_id); }}
                              data-testid={`clone-service-${service.service_id}`}
                              className="p-2 text-slate-400 hover:text-green-600 hover:bg-slate-50 rounded-lg transition-colors"
                              title="Clone service"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <DeleteButton
                              onConfirm={() => handleDelete(service.service_id)}
                              className="p-2 rounded-lg"
                              title="Delete service"
                              testId={`delete-service-${service.service_id}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {service.notes && (
                      <p className="text-xs text-slate-500 line-clamp-2 mb-3">{service.notes}</p>
                    )}
                    {assignment && (
                      <div className="pt-3 border-t border-slate-100 mt-3" onClick={(e) => e.stopPropagation()} data-testid={`assignment-${service.service_id}`}>
                        <p className="text-xs text-green-600 font-medium mb-2">Assigned: {assignment.roles.join(', ')}</p>
                        {service.date < today ? (
                          <p className="text-xs text-slate-400 italic">This service has already passed.</p>
                        ) : (
                        <div className="inline-flex rounded-md overflow-hidden border border-slate-200" data-testid={`response-toggle-${service.service_id}`}>
                          <button onClick={() => handleRespond(service.service_id, assignment.status === 'accepted' ? 'pending' : 'accepted', 'accept')} data-testid={`accept-btn-${service.service_id}`}
                            disabled={responding?.id === service.service_id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                              assignment.status === 'accepted' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'
                            }`}>
                            {responding?.id === service.service_id && responding?.action === 'accept'
                              ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              : <Check className="w-3 h-3" />
                            }
                            {assignment.status === 'accepted' ? 'Accepted' : 'Accept'}
                          </button>
                          <button onClick={() => handleRespond(service.service_id, assignment.status === 'declined' ? 'pending' : 'declined', 'decline')} data-testid={`decline-btn-${service.service_id}`}
                            disabled={responding?.id === service.service_id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                              assignment.status === 'declined' ? 'bg-rose-600 text-white' : 'bg-white text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                            }`}>
                            {responding?.id === service.service_id && responding?.action === 'decline'
                              ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              : <X className="w-3 h-3" />
                            }
                            {assignment.status === 'declined' ? 'Declined' : 'Decline'}
                          </button>
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1 mt-2" />
            {loadingMore && (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700 mx-auto" />
              </div>
            )}
            </>
          )
        )}

        {/* Create Service Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={serviceModal.onOpenChange}>
          <DialogContent className="bg-white mx-4 sm:mx-auto max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Service</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Service Type *</Label>
                <Select
                  value={formData.service_type}
                  onValueChange={(val) => setFormData({ ...formData, service_type: val })}
                  required
                >
                  <SelectTrigger data-testid="service-type-select" className="mt-1">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {serviceTypes.map((t) => (
                      <SelectItem key={t.service_type_id} value={t.name}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Service Name (optional)</Label>
                <Input
                  id="name"
                  data-testid="service-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Leave blank to use service type as name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="service-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    data-testid="service-time-input"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="service-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special instructions or notes..."
                  rows={3}
                />
              </div>
              <Button type="submit" data-testid="submit-service-btn" className="w-full"
                disabled={!formData.service_type}
              >
                Add Service
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    {showAvailability && <AvailabilityModal onClose={() => setShowAvailability(false)} />}
    </>
  );
};

export default Services;
